/**
 * refsCrudApi — RTK Query CRUD slices cho 9 ref tables (Sprint 5).
 *
 * URL pattern: /purchase/refs/{name}/{list,create,{id},update,delete}
 *
 * Một slice riêng cho mỗi ref để giữ type signature riêng biệt.
 * Tag enhanced shared: 'Refs' (mỗi ref có id namespace prefix).
 *
 * Refs (9):
 *   - certifications
 *   - delivery-places
 *   - delivery-terms
 *   - packages
 *   - adjust-lots
 *   - licenses (admin)
 *   - qualities  (list_and_valo)
 *   - updates
 *   - calendars  (market_calendar)
 */
import { baseApi } from './baseApi'
import type {
  AdjustLot,
  AdminLicense,
  Certification,
  DataResponse,
  DeliveryPlace,
  DeliveryTerm,
  MarketCalendar,
  MessageResponse,
  PageResponse,
  Package,
  Quality,
  RefListQuery,
  Update,
} from '@/types/api'

const refsEnhanced = baseApi.enhanceEndpoints({ addTagTypes: ['Refs'] })

/** Helper builder: tạo 4 endpoints CRUD chuẩn cho 1 ref name. */
function buildRefEndpoints<T, Id extends string | number>(
  name: string,
  /** Lấy id (PK) từ row — dùng cho PUT/DELETE URL. */
  _getId: (row: T) => Id,
) {
  // KHÔNG dùng trực tiếp trong slice; mỗi slice gọi riêng để giữ tag id
  // nhất quán. Đây chỉ là interface gợi ý — implement inline trong từng slice.
  return { name, _getId }
}
void buildRefEndpoints // tránh tree-shake warning

// ---------- 1. certifications ----------
const certificationsRefApi = refsEnhanced.injectEndpoints({
  endpoints: (b) => ({
    listCertifications: b.query<PageResponse<Certification>, RefListQuery | void>({
      query: (params) => ({
        url: '/purchase/refs/certifications/list',
        params: params ?? {},
      }),
      providesTags: [{ type: 'Refs', id: 'certifications-LIST' }],
    }),
    createCertification: b.mutation<DataResponse<Certification>, Certification>({
      query: (body) => ({
        url: '/purchase/refs/certifications/create',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'Refs', id: 'certifications-LIST' }],
    }),
    updateCertification: b.mutation<
      DataResponse<Certification>,
      { id: string; body: Partial<Certification> }
    >({
      query: ({ id, body }) => ({
        url: `/purchase/refs/certifications/${encodeURIComponent(id)}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: [{ type: 'Refs', id: 'certifications-LIST' }],
    }),
    deleteCertification: b.mutation<MessageResponse, string>({
      query: (id) => ({
        url: `/purchase/refs/certifications/${encodeURIComponent(id)}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'Refs', id: 'certifications-LIST' }],
    }),
  }),
})

// ---------- 2. delivery-places ----------
const deliveryPlacesRefApi = refsEnhanced.injectEndpoints({
  endpoints: (b) => ({
    listDeliveryPlacesCrud: b.query<PageResponse<DeliveryPlace>, RefListQuery | void>({
      query: (params) => ({
        url: '/purchase/refs/delivery-places/list',
        params: params ?? {},
      }),
      providesTags: [{ type: 'Refs', id: 'delivery-places-LIST' }],
    }),
    createDeliveryPlace: b.mutation<DataResponse<DeliveryPlace>, DeliveryPlace>({
      query: (body) => ({
        url: '/purchase/refs/delivery-places/create',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'Refs', id: 'delivery-places-LIST' }],
    }),
    updateDeliveryPlace: b.mutation<
      DataResponse<DeliveryPlace>,
      { id: string; body: Partial<DeliveryPlace> }
    >({
      query: ({ id, body }) => ({
        url: `/purchase/refs/delivery-places/${encodeURIComponent(id)}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: [{ type: 'Refs', id: 'delivery-places-LIST' }],
    }),
    deleteDeliveryPlace: b.mutation<MessageResponse, string>({
      query: (id) => ({
        url: `/purchase/refs/delivery-places/${encodeURIComponent(id)}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'Refs', id: 'delivery-places-LIST' }],
    }),
  }),
})

// ---------- 3. delivery-terms ----------
const deliveryTermsRefApi = refsEnhanced.injectEndpoints({
  endpoints: (b) => ({
    listDeliveryTermsCrud: b.query<PageResponse<DeliveryTerm>, RefListQuery | void>({
      query: (params) => ({
        url: '/purchase/refs/delivery-terms/list',
        params: params ?? {},
      }),
      providesTags: [{ type: 'Refs', id: 'delivery-terms-LIST' }],
    }),
    createDeliveryTerm: b.mutation<DataResponse<DeliveryTerm>, DeliveryTerm>({
      query: (body) => ({
        url: '/purchase/refs/delivery-terms/create',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'Refs', id: 'delivery-terms-LIST' }],
    }),
    updateDeliveryTerm: b.mutation<
      DataResponse<DeliveryTerm>,
      { id: string; body: Partial<DeliveryTerm> }
    >({
      query: ({ id, body }) => ({
        url: `/purchase/refs/delivery-terms/${encodeURIComponent(id)}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: [{ type: 'Refs', id: 'delivery-terms-LIST' }],
    }),
    deleteDeliveryTerm: b.mutation<MessageResponse, string>({
      query: (id) => ({
        url: `/purchase/refs/delivery-terms/${encodeURIComponent(id)}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'Refs', id: 'delivery-terms-LIST' }],
    }),
  }),
})

// ---------- 4. packages ----------
const packagesRefApi = refsEnhanced.injectEndpoints({
  endpoints: (b) => ({
    listPackagesCrud: b.query<PageResponse<Package>, RefListQuery | void>({
      query: (params) => ({
        url: '/purchase/refs/packages/list',
        params: params ?? {},
      }),
      providesTags: [{ type: 'Refs', id: 'packages-LIST' }],
    }),
    createPackage: b.mutation<DataResponse<Package>, Package>({
      query: (body) => ({
        url: '/purchase/refs/packages/create',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'Refs', id: 'packages-LIST' }],
    }),
    updatePackage: b.mutation<
      DataResponse<Package>,
      { id: string; body: Partial<Package> }
    >({
      query: ({ id, body }) => ({
        url: `/purchase/refs/packages/${encodeURIComponent(id)}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: [{ type: 'Refs', id: 'packages-LIST' }],
    }),
    deletePackage: b.mutation<MessageResponse, string>({
      query: (id) => ({
        url: `/purchase/refs/packages/${encodeURIComponent(id)}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'Refs', id: 'packages-LIST' }],
    }),
  }),
})

// ---------- 5. adjust-lots ----------
const adjustLotsRefApi = refsEnhanced.injectEndpoints({
  endpoints: (b) => ({
    listAdjustLots: b.query<PageResponse<AdjustLot>, RefListQuery | void>({
      query: (params) => ({
        url: '/purchase/refs/adjust-lots/list',
        params: params ?? {},
      }),
      providesTags: [{ type: 'Refs', id: 'adjust-lots-LIST' }],
    }),
    createAdjustLot: b.mutation<DataResponse<AdjustLot>, AdjustLot>({
      query: (body) => ({
        url: '/purchase/refs/adjust-lots/create',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'Refs', id: 'adjust-lots-LIST' }],
    }),
    updateAdjustLot: b.mutation<
      DataResponse<AdjustLot>,
      { id: string | number; body: Partial<AdjustLot> }
    >({
      query: ({ id, body }) => ({
        url: `/purchase/refs/adjust-lots/${encodeURIComponent(String(id))}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: [{ type: 'Refs', id: 'adjust-lots-LIST' }],
    }),
    deleteAdjustLot: b.mutation<MessageResponse, string | number>({
      query: (id) => ({
        url: `/purchase/refs/adjust-lots/${encodeURIComponent(String(id))}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'Refs', id: 'adjust-lots-LIST' }],
    }),
  }),
})

// ---------- 6. licenses (admin) ----------
const licensesAdminRefApi = refsEnhanced.injectEndpoints({
  endpoints: (b) => ({
    listAdminLicenses: b.query<PageResponse<AdminLicense>, RefListQuery | void>({
      query: (params) => ({
        url: '/purchase/refs/licenses/list',
        params: params ?? {},
      }),
      providesTags: [{ type: 'Refs', id: 'licenses-LIST' }],
    }),
    createAdminLicense: b.mutation<DataResponse<AdminLicense>, Partial<AdminLicense>>({
      query: (body) => ({
        url: '/purchase/refs/licenses/create',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'Refs', id: 'licenses-LIST' }],
    }),
    updateAdminLicense: b.mutation<
      DataResponse<AdminLicense>,
      { id: number; body: Partial<AdminLicense> }
    >({
      query: ({ id, body }) => ({
        url: `/purchase/refs/licenses/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: [{ type: 'Refs', id: 'licenses-LIST' }],
    }),
    deleteAdminLicense: b.mutation<MessageResponse, number>({
      query: (id) => ({
        url: `/purchase/refs/licenses/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'Refs', id: 'licenses-LIST' }],
    }),
  }),
})

// ---------- 7. qualities (list_and_valo) ----------
const qualitiesRefApi = refsEnhanced.injectEndpoints({
  endpoints: (b) => ({
    listQualitiesCrud: b.query<PageResponse<Quality>, RefListQuery | void>({
      query: (params) => ({
        url: '/purchase/refs/qualities/list',
        params: params ?? {},
      }),
      providesTags: [{ type: 'Refs', id: 'qualities-LIST' }],
    }),
    createQuality: b.mutation<DataResponse<Quality>, Quality>({
      query: (body) => ({
        url: '/purchase/refs/qualities/create',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'Refs', id: 'qualities-LIST' }],
    }),
    updateQuality: b.mutation<
      DataResponse<Quality>,
      { id: string; body: Partial<Quality> }
    >({
      query: ({ id, body }) => ({
        url: `/purchase/refs/qualities/${encodeURIComponent(id)}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: [{ type: 'Refs', id: 'qualities-LIST' }],
    }),
    deleteQuality: b.mutation<MessageResponse, string>({
      query: (id) => ({
        url: `/purchase/refs/qualities/${encodeURIComponent(id)}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'Refs', id: 'qualities-LIST' }],
    }),
  }),
})

// ---------- 8. updates ----------
const updatesRefApi = refsEnhanced.injectEndpoints({
  endpoints: (b) => ({
    listUpdates: b.query<PageResponse<Update>, RefListQuery | void>({
      query: (params) => ({
        url: '/purchase/refs/updates/list',
        params: params ?? {},
      }),
      providesTags: [{ type: 'Refs', id: 'updates-LIST' }],
    }),
    createUpdate: b.mutation<DataResponse<Update>, Update>({
      query: (body) => ({
        url: '/purchase/refs/updates/create',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'Refs', id: 'updates-LIST' }],
    }),
    updateUpdate: b.mutation<
      DataResponse<Update>,
      { id: number; body: Partial<Update> }
    >({
      query: ({ id, body }) => ({
        url: `/purchase/refs/updates/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: [{ type: 'Refs', id: 'updates-LIST' }],
    }),
    deleteUpdate: b.mutation<MessageResponse, number>({
      query: (id) => ({
        url: `/purchase/refs/updates/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'Refs', id: 'updates-LIST' }],
    }),
  }),
})

// ---------- 9. calendars (market_calendar) ----------
const calendarsRefApi = refsEnhanced.injectEndpoints({
  endpoints: (b) => ({
    listCalendars: b.query<PageResponse<MarketCalendar>, RefListQuery | void>({
      query: (params) => ({
        url: '/purchase/refs/calendars/list',
        params: params ?? {},
      }),
      providesTags: [{ type: 'Refs', id: 'calendars-LIST' }],
    }),
    createCalendar: b.mutation<DataResponse<MarketCalendar>, MarketCalendar>({
      query: (body) => ({
        url: '/purchase/refs/calendars/create',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'Refs', id: 'calendars-LIST' }],
    }),
    updateCalendar: b.mutation<
      DataResponse<MarketCalendar>,
      { id: string; body: Partial<MarketCalendar> }
    >({
      query: ({ id, body }) => ({
        url: `/purchase/refs/calendars/${encodeURIComponent(id)}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: [{ type: 'Refs', id: 'calendars-LIST' }],
    }),
    deleteCalendar: b.mutation<MessageResponse, string>({
      query: (id) => ({
        url: `/purchase/refs/calendars/${encodeURIComponent(id)}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'Refs', id: 'calendars-LIST' }],
    }),
  }),
})

// ---------- Re-exports ----------
export const {
  useListCertificationsQuery: useListCertificationsCrudQuery,
  useCreateCertificationMutation,
  useUpdateCertificationMutation,
  useDeleteCertificationMutation,
} = certificationsRefApi
export const {
  useListDeliveryPlacesCrudQuery,
  useCreateDeliveryPlaceMutation,
  useUpdateDeliveryPlaceMutation,
  useDeleteDeliveryPlaceMutation,
} = deliveryPlacesRefApi
export const {
  useListDeliveryTermsCrudQuery,
  useCreateDeliveryTermMutation,
  useUpdateDeliveryTermMutation,
  useDeleteDeliveryTermMutation,
} = deliveryTermsRefApi
export const {
  useListPackagesCrudQuery,
  useCreatePackageMutation,
  useUpdatePackageMutation,
  useDeletePackageMutation,
} = packagesRefApi
export const {
  useListAdjustLotsQuery,
  useCreateAdjustLotMutation,
  useUpdateAdjustLotMutation,
  useDeleteAdjustLotMutation,
} = adjustLotsRefApi
export const {
  useListAdminLicensesQuery,
  useCreateAdminLicenseMutation,
  useUpdateAdminLicenseMutation,
  useDeleteAdminLicenseMutation,
} = licensesAdminRefApi
export const {
  useListQualitiesCrudQuery,
  useCreateQualityMutation,
  useUpdateQualityMutation,
  useDeleteQualityMutation,
} = qualitiesRefApi
export const {
  useListUpdatesQuery,
  useCreateUpdateMutation,
  useUpdateUpdateMutation,
  useDeleteUpdateMutation,
} = updatesRefApi
export const {
  useListCalendarsQuery,
  useCreateCalendarMutation,
  useUpdateCalendarMutation,
  useDeleteCalendarMutation,
} = calendarsRefApi
